import React from "react";
import { Page, Layout, Card, Button, Stack, TextStyle } from "@shopify/polaris";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      reported: false,
      reportFailed: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("[Real ID] Fatal error:", error, errorInfo);
    this.reportError(error);
  }

  getRecentConsoleErrors() {
    return this._consoleErrors || [];
  }

  componentDidMount() {
    this._consoleErrors = [];
    const originalError = console.error;
    this._originalConsoleError = originalError;
    console.error = (...args) => {
      this._consoleErrors.push(
        args
          .map((a) => (typeof a === "string" ? a : JSON.stringify(a, null, 2)))
          .join(" ")
      );
      if (this._consoleErrors.length > 10) {
        this._consoleErrors.shift();
      }
      originalError.apply(console, args);
    };
  }

  componentWillUnmount() {
    if (this._originalConsoleError) {
      console.error = this._originalConsoleError;
    }
  }

  getErrorPayload(error) {
    const page = window.location.hash || "/";
    const pluginVersion = window.realIdApiSettings?.version || "unknown";
    const shopName =
      window.realIdApiSettings?.shopName ||
      window.realIdShopName ||
      "unknown";

    return {
      shopName,
      page,
      pluginVersion,
      error: error?.message || String(error),
      stack: error?.stack || null,
      userAgent: navigator.userAgent,
      consoleErrors: this.getRecentConsoleErrors(),
    };
  }

  reportError(error) {
    const coreApiHost = window.realIdApiSettings?.coreApiHost;
    if (!coreApiHost) return;

    const payload = this.getErrorPayload(error);

    fetch(`${coreApiHost}/api/wc/error-report`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (res.ok) {
          this.setState({ reported: true });
        } else {
          this.setState({ reportFailed: true });
        }
      })
      .catch(() => {
        this.setState({ reportFailed: true });
      });
  }

  handleCopyReport = () => {
    const { error } = this.state;
    const payload = this.getErrorPayload(error);

    const report = [
      `Real ID Plugin Error Report`,
      `---`,
      `Shop: ${payload.shopName}`,
      `Plugin Version: ${payload.pluginVersion}`,
      `Page: ${payload.page}`,
      `Time: ${new Date().toISOString()}`,
      `User Agent: ${payload.userAgent}`,
      ``,
      `Error: ${payload.error}`,
      ...(payload.stack ? [``, `Stack: ${payload.stack}`] : []),
      ...(payload.consoleErrors.length > 0
        ? [
            ``,
            `Recent console errors:`,
            ...payload.consoleErrors.map((e) => `  ${e}`),
          ]
        : []),
    ].join("\n");

    navigator.clipboard.writeText(report).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, copied, reported, reportFailed } = this.state;
    const page = window.location.hash || "/";
    const consoleErrors = this.getRecentConsoleErrors();

    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card title="Something went wrong" sectioned>
              <Stack vertical spacing="loose">
                <p>
                  The Real ID plugin encountered an unexpected error on this
                  page.
                  {reported
                    ? " This error has been automatically reported to our team."
                    : " Please copy the error report below and share it with support so we can fix this."}
                </p>

                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "6px",
                    padding: "12px 16px",
                  }}
                >
                  <p style={{ margin: "0 0 4px 0" }}>
                    <TextStyle variation="strong">Page:</TextStyle> {page}
                  </p>
                  <p style={{ margin: 0 }}>
                    <TextStyle variation="strong">Error:</TextStyle>{" "}
                    {error?.message || String(error)}
                  </p>
                </div>

                {reported && (
                  <div
                    style={{
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: "6px",
                      padding: "10px 14px",
                      fontSize: "13px",
                      color: "#166534",
                    }}
                  >
                    Error report sent to support automatically.
                  </div>
                )}

                {consoleErrors.length > 0 && (
                  <details>
                    <summary
                      style={{
                        cursor: "pointer",
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      Console logs ({consoleErrors.length})
                    </summary>
                    <pre
                      style={{
                        background: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        padding: "12px",
                        fontSize: "12px",
                        overflow: "auto",
                        maxHeight: "200px",
                        whiteSpace: "pre-wrap",
                        marginTop: "8px",
                      }}
                    >
                      {consoleErrors.join("\n\n")}
                    </pre>
                  </details>
                )}

                <Stack distribution="leading" spacing="tight">
                  <Button primary onClick={this.handleRefresh}>
                    Refresh page
                  </Button>
                  <Button onClick={this.handleCopyReport}>
                    {copied ? "Copied!" : "Copy error report"}
                  </Button>
                  <Button
                    url="https://getverdict.com/help/contact"
                    external
                  >
                    Contact support
                  </Button>
                </Stack>
              </Stack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }
}
